// tslint:disable no-string-based-set-timeout
import cx from 'classnames2';
import PopperJS from 'popper.js';
import React, { Children, cloneElement, createContext, useContext, useEffect, useRef, useState } from 'react';

import { IPopperProps, Popper } from '../Popper';
import { usePrevious, wrapEvent } from '../utils';

import styles from './styles.module.scss';

/**
 * Hook based idea:
 * const {referenceProps, popoverProps, arrowProps, state, actions} = usePopover(props).
 *
 * The popover must meet the AA Success Criterion
 * https://www.w3.org/WAI/WCAG21/Understanding/content-on-hover-or-focus.html
 * https://www.w3.org/WAI/WCAG21/Techniques/client-side-script/SCR39
 */

export interface IPopoverContextValue {
  popoverRef: React.MutableRefObject<HTMLElement>;
  placement: PopperJS.Placement;
  referenceRef: React.MutableRefObject<HTMLElement>;
  headerId: string;
  bodyId: string;
  popoverId: string;
  trigger: 'hover' | 'click';
  isOpen: boolean;
  onBlur: React.FocusEventHandler<HTMLElement>;
  closeOnEsc: boolean;
  initialFocusRef: boolean;
  isHoveringRef: React.MutableRefObject<boolean>;
  usePortal: boolean;
  onOpen(): void;
  onClose(): void;
  onToggle(): void;
}

const PopoverContext: React.Context<{}> = createContext({});
export const usePopoverContext = (): IPopoverContextValue | undefined => {
  const context = useContext(PopoverContext);
  if (context == null) {
    throw Error('usePopoverContext must be used within <Popover/>');
  }
  return context as IPopoverContextValue;
};

/////////////////////////////////////////////////////////////////////

export interface IPopoverTriggerProps {
  children: React.ReactNode;
}

export const PopoverTrigger: React.FunctionComponent<IPopoverTriggerProps> = ({ children }) => {
  const { referenceRef, popoverId, onToggle, trigger, onOpen, isOpen, onClose, isHoveringRef } = usePopoverContext();

  const child = Children.only(children) as any;
  let eventHandlers = {};

  if (trigger === 'click') {
    eventHandlers = {
      onClick: wrapEvent(child.props.onClick, onToggle)
    };
  }

  if (trigger === 'hover') {
    eventHandlers = {
      onFocus: wrapEvent(child.props.onFocus, onOpen),
      onKeyDown: wrapEvent(child.props.onKeyDown, (event) => {
        if (event.key === 'Escape') {
          setTimeout(onClose, 300);
        }
      }),
      onBlur: wrapEvent(child.props.onBlur, onClose),
      onMouseEnter: wrapEvent(child.props.onMouseEnter, () => {
        isHoveringRef.current = true;
        setTimeout(onOpen, 300);
      }),
      onMouseLeave: wrapEvent(child.props.onMouseLeave, () => {
        isHoveringRef.current = false;
        setTimeout(() => {
          if (isHoveringRef.current === false) {
            onClose();
          }
        }, 300);
      })
    };
  }

  return cloneElement(child, {
    'aria-haspopup': 'dialog',
    'aria-expanded': isOpen,
    'aria-controls': popoverId,
    ref: referenceRef,
    ...eventHandlers
  });
};

/////////////////////////////////////////////////////////////////////

export interface IPopoverContentProps extends IPopperProps {
  className?: string;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  gutter?: number;
  /**
   * If the `PopoverHeading` isn't rendered, use this prop to add
   * an accessible label to the popover.
   */
  'aria-label'?: string;
}

export const PopoverContent: React.FunctionComponent<IPopoverContentProps> = ({
  className,
  onKeyDown,
  onBlur: onBlurProp,
  onMouseLeave,
  onMouseEnter,
  onFocus,
  gutter = 4,
  'aria-label': ariaLabel,
  ...props
}) => {
  const {
    popoverRef,
    referenceRef,
    placement,
    popoverId,
    isOpen,
    onBlur,
    closeOnEsc,
    onClose,
    isHoveringRef,
    trigger,
    headerId,
    bodyId,
    usePortal
  } = usePopoverContext();

  let eventHandlers = {};
  let roleProps = {};

  if (trigger === 'click') {
    eventHandlers = {
      onBlur: wrapEvent(onBlurProp, onBlur)
    };

    roleProps = {
      role: 'dialog',
      'aria-modal': 'false'
    };
  }

  if (trigger === 'hover') {
    eventHandlers = {
      onMouseEnter: wrapEvent(onMouseEnter, () => {
        isHoveringRef.current = true;
      }),
      onMouseLeave: wrapEvent(onMouseLeave, () => {
        isHoveringRef.current = false;
        setTimeout(onClose, 300);
      })
    };

    roleProps = {
      role: 'tooltip'
    };
  }

  eventHandlers = {
    ...eventHandlers,
    onKeyDown: wrapEvent(onKeyDown, (event) => {
      if (event.key === 'Escape' && closeOnEsc) {
        // tslint:disable-next-line:no-unused-expression
        onClose && onClose();
      }
    })
  };

  return (
    <Popper
      ref={popoverRef}
      className={cx(styles.popoverContent, className)}
      usePortal={usePortal}
      isOpen={isOpen}
      placement={placement}
      anchorEl={referenceRef.current}
      id={popoverId}
      tabIndex={-1}
      modifiers={{ offset: { enabled: true, offset: `0, ${gutter}` } }}
      aria-hidden={!isOpen}
      aria-label={ariaLabel}
      aria-labelledby={headerId}
      aria-describedby={bodyId}
      {...roleProps}
      {...eventHandlers}
      {...props}
    />
  );
};

/////////////////////////////////////////////////////////////////////

type InternalState = { isOpen?: boolean; onClose?(): void };

type PopoverChildren =
  | {
      children: React.ReactNode;
    }
  | { children(props: InternalState): React.ReactNode };

export interface IPopover {
  /**
   * The html `id` attribute of the popover.
   * If not provided, we generate a unique id.
   *
   * This `id` is also used to auto-generate the `aria-labelledby`
   * and `aria-decribedby` attributes that points to the `PopoverHeader` and `PopoverBody`
   */
  id?: string;
  /**
   * If `true`, the popover will be opened in controlled mode.
   */
  isOpen?: boolean;
  /**
   * If `true`, the popover will be initially opened.
   */
  defaultIsOpen?: boolean;
  /**
   * The `ref` of the element that should receive focus when the popover opens.
   */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /**
   * The interaction that triggers the popover.
   *
   * `hover` - means the popover will open when you hover with mouse or
   * focus with keyboard on the popover trigger
   *
   * `click` - means the popover will open on click or
   * press `Enter` to `Space` on keyboard
   */
  trigger?: 'hover' | 'click';
  /**
   * If `true`, the popover will return focus to the trigger when it closes
   */
  returnFocusOnClose?: boolean;
  /**
   * The gap (in pixels) to apply between the popover and the target.
   * Used by `popper.js`
   */
  gutter?: number;
  /**
   * The placment of the popover
   */
  placement?: PopperJS.Placement;
  /**
   * If `true`, the popover will close when you blur out it by
   * clicking outside or tabbing out
   */
  closeOnBlur?: boolean;
  /**
   * If `true`, the popover will close when you hit the `Esc` key
   */
  closeOnEsc?: boolean;
  /**
   * If true the popover is displayed with a Portal.
   * Rendering content inside a Portal allows the popover content
   * to escape the physical bounds of its parent while still being
   * positioned correctly relative to its target
   */
  usePortal?: boolean;
  /**
   * Callback fired when the popover opens
   */
  onOpen?(): void;
  /**
   * Callback fired when the popover closes
   */
  onClose?(): void;
}

export type IPopoverProps = IPopover & PopoverChildren;

export const Popover: React.FC<IPopoverProps> = ({
  id,
  isOpen: isOpenProp,
  initialFocusRef,
  defaultIsOpen,
  usePortal = false,
  returnFocusOnClose = true,
  trigger = 'click',
  placement = 'bottom-start',
  children,
  closeOnBlur = true,
  closeOnEsc = true,
  onOpen: onOpenProp,
  onClose: onCloseProp
}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen || false);
  const { current: isControlled } = useRef(isOpenProp != null);

  const isHoveringRef: React.MutableRefObject<boolean> = useRef();

  const referenceRef: React.MutableRefObject<HTMLElement> = useRef();
  const popoverRef: React.MutableRefObject<HTMLElement> = useRef();

  // tslint:disable-next-line:variable-name
  const _isOpen = isControlled ? isOpenProp : isOpen;

  const onToggle = () => {
    if (!isControlled) {
      setIsOpen(!_isOpen);
    }

    if (!_isOpen === true) {
      // tslint:disable-next-line:no-unused-expression
      onOpenProp && onOpenProp();
    } else {
      // tslint:disable-next-line:no-unused-expression
      onCloseProp && onCloseProp();
    }
  };

  const onOpen = () => {
    if (!isControlled) {
      setIsOpen(true);
    }

    if (onOpenProp) {
      onOpenProp();
    }
  };

  const onClose = () => {
    if (!isControlled) {
      setIsOpen(false);
    }

    if (onCloseProp) {
      onCloseProp();
    }
  };

  const handleBlur = (event) => {
    let activeElement = event.relatedTarget;

    // Reacts blur event doesn't have relatedTarget in IE11
    if (activeElement === null) {
      activeElement = document.activeElement;
    }

    if (
      _isOpen &&
      closeOnBlur &&
      popoverRef.current &&
      referenceRef.current &&
      !popoverRef.current.contains(activeElement) &&
      !referenceRef.current.contains(activeElement)
    ) {
      onClose();
    }
  };

  const popoverId = id;

  const headerId = `${popoverId}-header`;
  const bodyId = `${popoverId}-body`;

  const prevIsOpen = usePrevious(_isOpen);

  useEffect(() => {
    if (_isOpen && trigger === 'click') {
      requestAnimationFrame(() => {
        if (initialFocusRef && initialFocusRef.current) {
          initialFocusRef.current.focus();
        } else {
          if (popoverRef.current) {
            popoverRef.current.focus();
          }
        }
      });
    }

    if (!_isOpen && prevIsOpen && trigger === 'click' && returnFocusOnClose) {
      if (referenceRef.current) {
        referenceRef.current.focus();
      }
    }
  }, [_isOpen, popoverRef, initialFocusRef, trigger, referenceRef, prevIsOpen, returnFocusOnClose]);

  const context = {
    popoverRef,
    placement,
    referenceRef,
    headerId,
    bodyId,
    popoverId,
    onOpen,
    onClose,
    onToggle,
    trigger,
    isOpen: _isOpen,
    onBlur: handleBlur,
    closeOnEsc,
    initialFocusRef,
    isHoveringRef,
    usePortal
  };

  return (
    <PopoverContext.Provider value={context}>
      {typeof children === 'function' ? children({ isOpen: _isOpen, onClose }) : children}
    </PopoverContext.Provider>
  );
};
